import { Injectable } from '@angular/core';
import { ShepherdService } from 'angular-shepherd';
import { AssessmentNames, TourEvents } from '../constants';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '../../auth/services/auth.service';
import { TourSessionStorage, TourStatus } from '../../auth/constants';

@Injectable({
  providedIn: 'root',
})
export class TourService {
  //last step: 49
  private counselorTourStepLoaded = new BehaviorSubject<number>(null);
  public tourActive = new BehaviorSubject<boolean>(false);

  constructor(private shepherdService: ShepherdService, private router: Router, private authService: AuthService) {}

  private get counselorTourStepLoaded$() {
    return this.counselorTourStepLoaded.asObservable();
  }

  setCounselorTourStepLoaded(loaded: number) {
    this.counselorTourStepLoaded.next(loaded);
  }

  get currentConsumerTourStep() {
    return this.counselorTourStepLoaded.getValue();
  }

  get isActiveTour() {
    return this.shepherdService.isActive;
  }

  get isActiveTour$() {
    return this.tourActive.asObservable();
  }

  startCounselorTour(onLogin: boolean = false) {
    this.shepherdService.confirmCancel = false;
    this.shepherdService.modal = true;

    this.shepherdService.defaultStepOptions = {
      cancelIcon: {
        enabled: true,
      },
      scrollTo: { behavior: 'smooth', block: 'center' },
    };

    const steps = [];

    this.addCounselorDashboardSteps(steps, onLogin);
    this.addCounselorStudentProfileSteps(steps);
    this.addCounselorGeneralAssessmentSteps(steps);
    this.AddCounselorCounselorInputSteps(steps);
    this.addCounselorAssessmentResultsSteps(steps);
    this.addCounselorResourceDirectorySteps(steps);
    this.addCounselorColumbiaAssessmentSteps(steps);
    this.addCounselorCaseSubmissionsTourSteps(steps);
    this.addCounselorProfileTourSteps(steps);
    this.addCounselorFinalThoughtsSteps(steps);

    this.shepherdService.addSteps(steps);
    Object.values(TourEvents).forEach((event) => {
      if (event === TourEvents.Complete || event === TourEvents.Close || event === TourEvents.Cancel) {
        this.shepherdService.tourObject.on(event, () => {
          this.tourActive.next(false);
          this.setCounselorTourStepLoaded(null);
          localStorage.setItem(this.getSessionStorageName(TourSessionStorage.CounselorTour), TourStatus.Complete);
          this.router.navigate(['**']).then(() => this.router.navigateByUrl('app/schools/dashboard'));
        });
      }
    });

    this.shepherdService.start();
    this.tourActive.next(true);
  }

  public addCounselorDashboardSteps(steps, onLogin: boolean): void {
    const self = this;
    steps.push(
      {
        id: 'tour-counselor-intro',
        text: [
          'Aspen is a behavioral health assessment tool to help students in our community be connected to the resources they need. In this platform, you will be able to complete vetted behavioral health assessments, be automatically suggested resources that fit their areas of need based on their location, search for students in your school, as well as be given leads for students that may need assistance through a variety of different channels.',
        ],
        title: 'Welcome to Aspen!',
        classes: 'shepherd-intro',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            if (onLogin) {
              self.router.navigate(['app/schools/dashboard']).then((_) => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 0) {
                    resolve();
                  }
                });
              });
            } else {
              self.router.navigate(['**']).then(() => {
                self.router.navigate(['app/schools/dashboard']).then((_) => {
                  self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                    if (result === 0) {
                      resolve();
                    }
                  });
                });
              });
            }
          });
        },
        buttons: [
          {
            text: 'Skip Tour',
            action: () => {
              return this.shepherdService.cancel();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-get-started',
        text: ['Let’s walk you through the system so you can get started! :)'],
        title: 'Welcome to Aspen!',
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-side-nav',
        text: [
          'Here is your school staff dashboard, which provides an overview of the behavioral health of your students that participant in the Aspen program.',
        ],
        title: 'School Staff Dashboard',
        attachTo: {
          element: '.tour-counselor-dashboard-side-nav',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-metrics',
        text: [
          'These metrics show the total completed assessments and assessment totals categorized as moderate or high risk completed in your district(s) and school(s).',
        ],
        title: 'School Staff Dashboard',
        attachTo: {
          element: '.tour-counselor-dashboard-metrics',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-leads',
        text: [
          'Student Lead Case submissions are students that need attention rather immediately. They show up in this table through a variety of different channels, such as a staff or student referral, as well as scoring a higher risk score on their self-screening. This will help you to intervene and follow up with students who might otherwise not be identified as needing support',
        ],
        title: 'Student Lead Cases',
        classes: 'shepherd-intro',
        attachTo: {
          element: '.tour-counselor-dashboard-leads',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-leads-side-nav',
        text: ['You also can access student lead cases from this side navigation tab.'],
        title: 'Student Lead Cases',
        attachTo: {
          element: '.tour-counselor-dashboard-leads-side-nav',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-student-search-icon',
        text: [
          'You can quick search for students using the magnifying glass icon by inputting their name or student ID',
        ],
        title: 'Search for Students',
        attachTo: {
          element: '.tour-counselor-dashboard-student-search-icon',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            const selector = document.getElementById('close-search');
            selector?.click();
            resolve();
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-student-search-dropdown-advanced',
        text: [
          'Or you can search for students by more advanced criteria by navigating to the participant advanced search tab here….',
        ],
        title: 'Search for Students',
        attachTo: {
          element: '.tour-counselor-dashboard-student-search-dropdown-advanced',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            const selector = document.getElementById('search-student-icon');
            selector.click();
            setTimeout(() => document.getElementById('student-search-input-box').focus(), 250);
            setTimeout(() => resolve(), 250);
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              const selector = document.getElementById('close-search');
              selector.click();
              this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-student-search-side-nav',
        text: ['And here.'],
        title: 'Search for Students',
        attachTo: {
          element: '.tour-counselor-dashboard-student-search-side-nav',
          on: 'right',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/dashboard']).then((_) => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 0) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-add-student-top-nav',
        text: ['You can also add a student'],
        title: 'Add a student',
        attachTo: {
          element: '.tour-counselor-dashboard-add-student-top-nav',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            setTimeout(() => resolve(), 250);
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-add-student-side-nav',
        text: ['And here.'],
        title: 'Add a Student',
        attachTo: {
          element: '.tour-counselor-dashboard-add-student-side-nav',
          on: 'right',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/dashboard']).then((_) => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 0) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-participant-advanced-search-intro',
        text: ['This is your advanced search where you can search by more specific criteria.'],
        title: 'Advanced Search for Students',
        attachTo: {
          element: '.tour-counselor-dashboard-participant-advanced-search-intro',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/participant-advanced-search']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 9) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-dashboard-participant-advanced-search',
        text: ['After you are finished searching, select the student in order to view their profile.'],
        title: 'Advanced Search for Students',
        attachTo: {
          element: '.tour-counselor-dashboard-participant-advanced-search',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/participant-advanced-search']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 9) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  public addCounselorStudentProfileSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-student-profile-contact-information',
        text: ['A student’s profile gives you an overview of information, including their contact information,'],
        title: 'Student Profile',
        attachTo: {
          element: '.tour-counselor-student-profile-contact-information',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/profile/student-detail'], { queryParams: { id: '00000' } }).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 11 || result === 12 || result === 46) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-student-profile-case-submission',
        text: ['their case submissions,'],
        title: 'Student Profile',
        attachTo: {
          element: '.tour-counselor-student-profile-case-submission',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
              if (result === 46 || 12) {
                resolve();
              }
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-student-profile-assessment-history',
        text: ['their screening history,'],
        title: 'Student Profile',
        attachTo: {
          element: '.tour-counselor-student-profile-assessment-history',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
              if (result === 12 || 46) {
                resolve();
              }
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-student-profile-documents',
        text: [
          'and allows you to upload and view documents, including their participation waiver, safety plan… any other document you’d like!',
        ],
        title: 'Student Profile',
        attachTo: {
          element: '.tour-counselor-student-profile-documents',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-student-profile-documents',
        text: [
          'However, Please note that any document you upload here will also be available to the student directly from their own account, so only upload documents you’d be comfortable with them seeing :)',
        ],
        title: 'Student Profile',
        attachTo: {
          element: '.tour-counselor-student-profile-documents',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/profile/student-detail'], { queryParams: { id: '00000' } }).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 12 || result === 11) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorGeneralAssessmentSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-general-assessment-side-nav',
        text: [
          `The ${AssessmentNames.GeneralLevel2} was created by school staff for school staff to help standardize the student behavioral health screening process.`,
        ],
        title: AssessmentNames.GeneralLevel2,
        attachTo: {
          element: '.tour-counselor-general-assessment-side-nav',
          on: 'right',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/general-level-2-assessment']).then(() => {
              self.counselorTourStepLoaded$.subscribe((result) => {
                if (result === 13) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-general-assessment-add-student',
        text: ['In order to begin, first you need to add a student to the screening.'],
        title: AssessmentNames.GeneralLevel2,
        attachTo: {
          element: '.tour-counselor-general-assessment-add-student',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-general-assessment-add-student-options',
        text: [
          'You can do this by searching for an existing student. If the student does not yet exist in the system. You will be prompted to add a new student to the system.',
        ],
        title: AssessmentNames.GeneralLevel2,
        attachTo: {
          element: '.tour-counselor-general-assessment-add-student-options',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-general-assessment-questions',
        text: [
          'After a student has been added, go through the screening with the student, asking certain questions at your discretion.',
        ],
        title: AssessmentNames.GeneralLevel2,
        attachTo: {
          element: '.tour-counselor-general-assessment-questions',
          on: 'top',
        },
        scrollTo: { behavior: 'smooth', block: 'start' },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-general-assessment-columbia-trigger',
        text: [
          `If a student answers yes to either of the suicide questions, you as the school staff have the option to navigate straight to the Columbia Suicide Risk Assessment. If you choose to do this, your progress will be saved on the ${AssessmentNames.GeneralLevel2}, and you can resume at a later time.`,
        ],
        title: `${AssessmentNames.GeneralLevel2} - Columbia Trigger`,
        attachTo: {
          element: '.tour-counselor-general-assessment-columbia-trigger',
          on: 'top',
        },
        scrollTo: { behavior: 'smooth', block: 'start' },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.counselorTourStepLoaded$.subscribe((result) => {
              if (result === 13) {
                resolve();
              }
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  AddCounselorCounselorInputSteps(steps) {
    const self = this;
    steps.push(
      {
        id: 'tour-counselor-counselor-input',
        text: [
          'Once all of the Level 2 questions have been answered, you will be routed to the school staff result input. This is where you will write your notes about the screening findings.',
        ],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input',
          on: 'top',
        },
        scrollTo: { behavior: 'smooth', block: 'start' },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-public-notes',
        text: [
          'School Staff Notes for Distribution are accessible and readable to other school staff in your school as well as the student and their parents.',
        ],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-public-notes',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-private-notes',
        text: ['Private Notes are readable and accessible only to you.'],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-private-notes',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-risk-level',
        text: [
          'The risk level ranks the student depending on the severity level of the situation. You can choose from Yellow (Low), Orange (Moderate) or Red (High) risk.',
        ],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-risk-level',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-areas-of-need',
        text: [
          'Areas of Need are categories selected based on your conversation with the student. Whether it be general mental health resources, eating disorders, etc, this will help Aspen to automatically match students to resources that will help their specific situation. This step is critical to automate the assistance process.',
        ],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-areas-of-need',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-priority-level',
        text: [
          'Lastly, select a priority level to indicate the urgency. Priority 1 is currently reserved for homicide or suicide risk, where Priority 2 is for all other situations.',
        ],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-priority-level',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-counselor-input-submit',
        text: ['Submit your input to complete the screening process.'],
        title: 'School Staff Input',
        attachTo: {
          element: '.tour-counselor-counselor-input-submit',
          on: 'top',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/general-level-2-assessment']).then(() => {
              self.counselorTourStepLoaded$.subscribe((result) => {
                if (result === 13) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorAssessmentResultsSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-assessment-results-intro',
        text: [
          'This page displays the results of the assessment, and can be used as an opportunity to have a conversation with your student about some of the things you discussed.',
        ],
        title: 'Assessment Results',
        attachTo: {
          element: '.tour-counselor-assessment-results-intro',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router
              .navigate(['app/schools/assessment-summary'], { queryParams: { resultId: '00000' } })
              .then(() => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 28) {
                    resolve();
                  }
                });
              });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-assessment-results-download',
        text: [
          'You also can download a copy of the assessment results in PDF format to send home to the student’s parents.',
        ],
        title: 'Assessment Results',
        attachTo: {
          element: '.tour-counselor-assessment-results-download',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-assessment-results-resource-directory',
        text: [
          'After the results have been discussed, navigate to the Resource Directory to view Aspen resources that can help your student in their specific situation.',
        ],
        title: 'Resource Matching',
        attachTo: {
          element: '.tour-counselor-assessment-results-resource-directory',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router
              .navigate(['app/schools/assessment-summary'], { queryParams: { resultId: '00000' } })
              .then(() => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 28) {
                    resolve();
                  }
                });
              });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorResourceDirectorySteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-resource-directory-intro',
        text: [
          'The resource directory are vetted resources by the Aspen Network to help students in our community. The resources are shown based off the address of your student, and the areas of need that you selected.',
        ],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-intro',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/resource-directory']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 31) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-branch',
        text: [
          'You can select a branch to filter by here. ' +
            'Filtering by a branch will show you behavioral health partners (resources that are specifically assigned to your school) that are assigned to that branch only. ' +
            'This will already be filtered to your assigned branch when accessing the page.',
        ],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-branch',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-partners',
        text: [
          'If your school has any behavioral health partners and you have either searched by name or filtered by district/organization, they are shown at the top of the list, and have a star to indicate them.',
        ],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-partners',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-hide-partners',
        text: ['You can hide behavioral health partners from showing up in your search by turning on this feature.'],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-hide-partners',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-filters',
        text: ['You can change the address, specialties, and other filters to best fit your needs.'],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-filters',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-name-search-input',
        text: [
          'You can search for a provider or behavioral health partner by entering their name here and clicking "Search Name"',
        ],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-name-search-input',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            const selector = document.getElementById('name-search');
            selector?.click();
            resolve();
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-download',
        text: ['Download resources here.'],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-download',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-resource-directory-side-nav',
        text: [
          'You can also access this page and search for resources at any time using the side navigation item here.',
        ],
        title: 'Resource Directory',
        attachTo: {
          element: '.tour-counselor-resource-directory-side-nav',
          on: 'right',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/resource-directory']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 31) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorColumbiaAssessmentSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-columbia-side-nav',
        text: ['The Columbia Assessment is a nationally standardized suicide risk assessment.'],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-side-nav',
          on: 'right',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/columbia-assessment']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 36 || result === 39) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-steps',
        text: [
          `The steps for conducting the assessment within Aspen are the same as the ${AssessmentNames.GeneralLevel2}, so these next few steps might be familiar to you, but let’s go through them together just in case.`,
        ],
        title: 'Columbia Assessment',
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-add-student',
        text: ['Add a student to the assessment.'],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-add-student',
          on: 'left',
        },
        scrollTo: { behavior: 'smooth', block: 'center' },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-progress-bar',
        text: [
          'At the top of the assessment, you will see a progress bar and a risk severity gauge. This is automated logic integrated into the Aspen system which aids the school staff in assigning the suicide risk of the student. This logic is per the official Columbia Assessment.',
        ],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-progress-bar',
          on: 'bottom',
        },
        scrollTo: false,
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
              if (result === 39) {
                window.scroll({
                  top: 0,
                  left: 0,
                  behavior: 'smooth',
                });
                resolve();
              }
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-severity-gauge-and-percent-completed',
        text: [
          'Complete the assessment questions per your guidance, see your progress and watch the severity level change based off the answers.',
        ],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-severity-gauge-and-percent-completed',
          on: 'bottom',
        },
        scrollTo: false,
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-submit',
        text: ['Submit the assessment,'],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-submit',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-counselor-input',
        text: ['and complete the school staff result input.'],
        title: 'Columbia Assessment',
        attachTo: {
          element: '.tour-counselor-columbia-counselor-input',
          on: 'top',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['app/schools/columbia-assessment']).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 36 || result === 39) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-result-page',
        text: ['After the assessment has been submitted, you will be routed to the assessment result page.'],
        title: 'Columbia Assessment Results',
        attachTo: {
          element: '.tour-counselor-assessment-results-intro',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router
              .navigate(['app/schools/assessment-summary'], { queryParams: { resultId: '00000' } })
              .then(() => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 28) {
                    resolve();
                  }
                });
              });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-columbia-resource-directory',
        text: ['Continue to the Resource Directory for further action.'],
        title: 'Resource Matching',
        attachTo: {
          element: '.tour-counselor-assessment-results-resource-directory',
          on: 'top',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router
              .navigate(['app/schools/assessment-summary'], { queryParams: { resultId: '00000' } })
              .then(() => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 28) {
                    resolve();
                  }
                });
              });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorCaseSubmissionsTourSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-case-submissions-table',
        text: [
          'Student Cases are students that are identified through a variety of different methods to need assistance as soon as possible. Student cases currently can be submitted to the system when a student is categorized as moderate-high risk through a self-assessment, or if they are reported through a public referral by a colleague or friend.',
        ],
        title: 'Case Submissions',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate([`app/schools/case`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 0) {
                  resolve();
                }
              });
            });
          });
        },
        attachTo: {
          element: '.tour-counselor-case-submissions-table',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-table',
        text: [
          'You can view a full list of student lead cases that are assigned to you in this table. High priority cases will appear in red.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-table',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-table-dismiss',
        text: ['You can dismiss a case by clicking here. Doing so will remove the student lead case from your view.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-table-dismiss',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-table-detail',
        text: ['Click a row to see more details about the student lead case and leave comments.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-table-detail',
          on: 'bottom',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate([`app/schools/case`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 0) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail',
        text: [
          'The case detail will provide you all details of a student lead case to aid you in helping the student.',
        ],
        title: 'Case Submissions',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate([`app/schools/case/detail/0`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 48) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail',
        text: ['First, we will look at a case generated from a self-screening.'],
        title: 'Case Submissions',
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-status',
        text: [
          'You can view and edit the case status here. When changing the case status, a case note will be auto-generated ' +
            'for other users to see the status change and who made it.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-status',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-participant',
        text: ['In this section, you will find information about the student who took the screening.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-participant',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-information-view-profile',
        text: ['To view their full profile and see further details about the student, click "View Profile."'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-information-view-profile',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-case',
        text: ['In this section, you will find information about the case.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-case',
          on: 'left',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-assignee',
        text: [
          'To assign a school staff member or hotline support user to the case (or change the assignee), click the pencil icon. ' +
            'This will allow other school staff and hotline support users to know this case is being taken care of.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-assignee',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-screening-history',
        text: ['Click "See Screening History" to view the students past screenings'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-screening-history',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-notes',
        text: ['Here, you can leave case notes and view notes made by other users.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-notes',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-initiation-comment',
        text: [
          'Each case will have a case initiation comment. The case initiation comment on a self-screening case is auto-generated, ' +
            'providing you details of the screening result.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-initiation-comment',
          on: 'top',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            if (self.currentConsumerTourStep !== 48) {
              self.router.navigate(['/']).then(() => {
                self.router.navigate([`app/schools/case/detail/0`]).then(() => {
                  self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                    if (result === 48) {
                      resolve();
                    }
                  });
                });
              });
            } else {
              resolve();
            }
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-upload-documents',
        text: ['Here, you can upload documents related to the case.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-upload-documents',
          on: 'top',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail',
        text: ['Next, we will look at a case generated from a public referral!'],
        title: 'Case Submissions',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['/']).then(() => {
              self.router.navigate([`app/schools/case/detail/1`]).then(() => {
                self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                  if (result === 49) {
                    resolve();
                  }
                });
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail',
        text: ['A student can be referred by a staff member, another student, or a first responder.'],
        title: 'Case Submissions',
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-participant',
        text: ['A public referral will only provide the name of the student.'],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-participant',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-search-student',
        text: [
          'To link the case to an existing student, click "Search for Matching Student." You will then be able to choose from a list of students in your organization. ' +
            'Be careful when doing this, you will not be able to search again once a student has been selected. This action cannot be undone.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-search-student',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-case-submissions-detail-initiation-comment',
        text: [
          'The case initiation comment on a public referral is the comment left from the person referring the student.',
        ],
        title: 'Case Submissions',
        attachTo: {
          element: '.tour-counselor-case-submissions-detail-initiation-comment',
          on: 'top',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate([`app/schools/case/detail/1`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 49) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      }
    );
  }

  addCounselorProfileTourSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-profile-side-nav',
        text: ['Lastly, you can view your own profile'],
        title: 'Profile',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            const userId = self.authService.authUser.userId;
            self.router.navigate([`app/schools/profile/user-detail/${userId}`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 45) {
                  resolve();
                }
              });
            });
          });
        },
        attachTo: {
          element: '.tour-counselor-profile-side-nav',
          on: 'right',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-profile-overview',
        text: ['Here you will see your core system information and are able to edit your contact information.'],
        title: 'Profile',
        attachTo: {
          element: '.tour-counselor-profile-overview',
          on: 'left',
        },
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            const userId = self.authService.authUser.userId;
            self.router.navigate([`app/schools/profile/user-detail/${userId}`]).then(() => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 45) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              this.shepherdService.next();
            },
          },
        ],
      }
    );
  }
  addCounselorFinalThoughtsSteps(steps) {
    const self = this;

    steps.push(
      {
        id: 'tour-counselor-final-thoughts',
        text: [
          'Now that you’ve had a walk-through of the Aspen system, play around with it a bit, and you’ll be an expert in no time :)',
        ],
        title: 'Final Thoughts',
        beforeShowPromise: () => {
          return new Promise<void>(function (resolve) {
            self.router.navigate(['./app/schools/dashboard']).then((_) => {
              self.counselorTourStepLoaded$.pipe(take(2)).subscribe((result) => {
                if (result === 0) {
                  resolve();
                }
              });
            });
          });
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-final-thoughts-start-tour-button',
        text: [
          'If at any point you need a refresher, you can trigger this tutorial at any point by clicking the "Start Tour" button under your user profile menu.',
        ],
        title: 'Final Thoughts',
        attachTo: {
          element: '.tour-counselor-final-thoughts-start-tour-button',
          on: 'bottom',
        },
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Next',
            action: () => {
              return this.shepherdService.next();
            },
          },
        ],
      },
      {
        id: 'tour-counselor-final-thoughts-thank-you',
        text: ['Thanks for walking-through the system with us, and welcome to Aspen!'],
        title: 'Final Thoughts',
        buttons: [
          {
            text: 'Back',
            action: () => {
              return this.shepherdService.back();
            },
            classes: 'shepherd-button-secondary',
          },
          {
            text: 'Complete Tour',
            action: () => {
              return this.shepherdService.complete();
            },
          },
        ],
      }
    );
  }

  public getSessionStorageName(type: TourSessionStorage): string {
    return `${type}.${this.authService.authUser?.userId}`;
  }
}
